import React, { useMemo, useState } from "react";
import "./disease-activity.scss";
import { Input, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTrashAlt, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { CALCULATORS, getCalculatorById } from "../../../utils/rheumatology/calculators";
import CalculatorRunner from "./calculator-runner";
import { removeVisitScore } from "../../../service/api/visit";
import { Toast } from "../../../utils/SwalUti";

// Accent colour + short badge per disease area, for the list avatars.
const GROUP_STYLE = {
    "Rheumatoid arthritis": { color: "#13ac81", abbr: "RA" },
    "Axial spondyloarthritis": { color: "#3b82f6", abbr: "SpA" },
    "Psoriatic arthritis": { color: "#8b5cf6", abbr: "PsA" },
    "Sjögren's syndrome": { color: "#f59e0b", abbr: "SjS" },
    "Systemic lupus erythematosus": { color: "#ec4899", abbr: "SLE" },
    Vasculitis: { color: "#ef4444", abbr: "Vas" },
    "Risk assessment": { color: "#0891b2", abbr: "Risk" },
};
const groupStyle = (g) => GROUP_STYLE[g] || { color: "#6b7280", abbr: "—" };

// Rows saved before computedAt existed sort as oldest; on a tie the later array
// element wins, which is insertion order because saves $push.
const scoreTime = (s) => {
    const t = new Date(s?.computedAt || 0).getTime();
    return Number.isFinite(t) ? t : 0;
};

const DiseaseActivity = ({ visitDetails, updateState }) => {
    const [search, setSearch] = useState("");

    // Default to the first ready calculator.
    const firstReadyId = useMemo(
        () => CALCULATORS.find((c) => c.status === "ready")?.id,
        []
    );
    const [selectedId, setSelectedId] = useState(firstReadyId);
    // A specific saved row pinned by clicking its chip; null means auto-pick the newest.
    const [editingScoreId, setEditingScoreId] = useState(null);

    const savedScores = visitDetails?.diseaseActivityScores || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return CALCULATORS;
        return CALCULATORS.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                c.subtitle?.toLowerCase().includes(q) ||
                c.group?.toLowerCase().includes(q)
        );
    }, [search]);

    // Group by disease area, preserving registry order.
    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach((c) => {
            const g = c.group || "Other";
            if (!map.has(g)) map.set(g, []);
            map.get(g).push(c);
        });
        return Array.from(map.entries());
    }, [filtered]);

    const selected = selectedId ? getCalculatorById(selectedId) : null;

    // One score per calculator VARIANT per visit (e.g. DAS28 ESR and CRP coexist) — the
    // runner resolves the actual save target by the selected variant. Opening a calculator
    // picks up the newest saved row as a starting point; clicking a chip pins that specific
    // row so every legacy duplicate stays individually editable rather than being shadowed.
    const editTarget = useMemo(() => {
        if (!selectedId) return null;
        const pinned = editingScoreId && savedScores.find((s) => s._id === editingScoreId);
        if (pinned && pinned.calculatorId === selectedId) return pinned;
        const matches = savedScores.filter((s) => s.calculatorId === selectedId);
        if (!matches.length) return null;
        return matches.reduce((latest, s) => (scoreTime(s) >= scoreTime(latest) ? s : latest));
    }, [savedScores, selectedId, editingScoreId]);

    const openScore = (score) => {
        if (getCalculatorById(score.calculatorId)?.status !== "ready") return;
        setSelectedId(score.calculatorId);
        setEditingScoreId(score._id);
    };

    const onRemove = (scoreId) => {
        if (!visitDetails?._id) return;
        removeVisitScore(scoreId, visitDetails._id)
            .then((res) => {
                if (res.status === 200) {
                    if (scoreId === editingScoreId) setEditingScoreId(null);
                    updateState?.(res.data);
                    Toast.fire({ icon: "success", title: "Score removed" });
                }
            })
            .catch(() => Toast.fire({ icon: "error", title: "Could not remove score" }));
    };

    return (
        <div className="disease-activity-section">
            {/* Left pane — tool list */}
            <aside className="da-sidebar">
                <div className="da-sidebar-head">
                    <div className="da-title">Disease activity</div>
                    <Input
                        className="da-search"
                        allowClear
                        prefix={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="da-list">
                    {groups.map(([group, items]) => {
                        const gs = groupStyle(group);
                        return (
                            <div className="calc-group" key={group}>
                                <p className="group-title" style={{ color: gs.color }}>{group}</p>
                                {items.map((c) => {
                                    const isReady = c.status === "ready";
                                    const isActive = isReady && c.id === selectedId;
                                    return (
                                        <div
                                            key={c.id}
                                            className={`calc-item ${isActive ? "active" : ""} ${isReady ? "" : "disabled"}`}
                                            style={isActive ? { background: `${gs.color}1a` } : {}}
                                            onClick={() => {
                                                if (!isReady) return;
                                                setSelectedId(c.id);
                                                // Drop any pinned row so the newest is picked.
                                                setEditingScoreId(null);
                                            }}
                                        >
                                            <span
                                                className="calc-avatar"
                                                style={{ background: `${gs.color}1a`, color: gs.color }}
                                            >
                                                {gs.abbr}
                                            </span>
                                            <div className="calc-text">
                                                <span className="calc-name">{c.name}</span>
                                                <span className="calc-sub">{c.subtitle}</span>
                                            </div>
                                            {isReady ? (
                                                isActive && (
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className="calc-chevron"
                                                        style={{ color: gs.color }}
                                                    />
                                                )
                                            ) : (
                                                <Tag className="soon-tag">Soon</Tag>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {groups.length === 0 && <p className="empty">No matches.</p>}
                </div>

                {savedScores.length > 0 && (
                    <div className="saved-scores">
                        <p className="saved-title">Saved on this visit</p>
                        {savedScores.map((s) => {
                            const gs = groupStyle(getCalculatorById(s.calculatorId)?.group);
                            return (
                                <div
                                    className={`saved-chip ${s._id === editTarget?._id ? "active" : ""}`}
                                    key={s._id}
                                    style={{ borderLeftColor: gs.color }}
                                    onClick={() => openScore(s)}
                                >
                                    <div className="saved-info">
                                        <span className="saved-name">
                                            {s.name}
                                            {s.variant ? <span className="saved-variant"> · {s.variant}</span> : null}
                                        </span>
                                        <span className="saved-cat">{s.category}</span>
                                    </div>
                                    <span className="saved-value" style={{ color: gs.color }}>{s.score}</span>
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(s._id);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </aside>

            {/* Right pane — selected tool */}
            <main className="da-content">
                {selected && selected.status === "ready" ? (
                    <CalculatorRunner
                        key={`${selected.id}:${editTarget?._id || "new"}`}
                        definition={selected}
                        visitDetails={visitDetails}
                        existingScore={editTarget}
                        calculatorScores={savedScores.filter((s) => s.calculatorId === selected.id)}
                        onSaved={updateState}
                    />
                ) : (
                    <div className="da-empty">Select a calculator from the list.</div>
                )}
            </main>
        </div>
    );
};

export default DiseaseActivity;
